import {
  getFormProps,
  useForm,
  useInputControl,
  unstable_Control as Control,
} from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { Form } from '@remix-run/react'
import { z } from 'zod'

import { Checkbox, InputError, Link, Textarea, TextInput } from '~/components'
import { FieldLabel } from '~/features/common'

import { contactFormSchema } from './schema'

type ContactType = {
  value: z.infer<typeof contactFormSchema>['contactInquiryType'][number]
  label: string
}

const CONTACT_TYPE_OPTIONS: ContactType[] = [
  { value: 'SERVICE_INQUIRY', label: 'サービスに関するお問い合わせ' },
  { value: 'SITE_VISIT_REQUEST', label: '内見希望' },
  { value: 'AVAILABILITY_CHECK', label: '空き予約' },
  { value: 'OTHER', label: 'その他' },
]

type FieldWrapperProps = {
  children: React.ReactNode
  ariaLabel?: string
}

const FieldWrapper = ({ children, ariaLabel }: FieldWrapperProps) => {
  return (
    <div aria-label={ariaLabel} className="flex flex-col gap-y-2">
      {children}
    </div>
  )
}

export const ContactForm = () => {
  const [form, fields] = useForm({
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: contactFormSchema })
    },
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
  })

  const checkboxControl = useInputControl(fields.agree)

  return (
    <Form
      className="flex flex-col gap-y-10"
      method="post"
      {...getFormProps(form)}
      data-testid="office-contact-form"
    >
      <FieldWrapper ariaLabel="お問い合わせ種別">
        <FieldLabel label="お問い合わせ種別" required />
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {CONTACT_TYPE_OPTIONS.map((option) => (
            <Control
              key={option.value}
              meta={{
                key: fields.contactInquiryType.key,
              }}
              render={(control) => (
                <div className="flex flex-shrink-0 items-center gap-2">
                  <Checkbox
                    checked={control.value == option.value}
                    data-testid={`contact-type-${option.value}`}
                    id={`${fields.contactInquiryType.id}-${option.value}`}
                    label={option.label}
                    name={fields.contactInquiryType.name}
                    onBlur={control.blur}
                    onChange={() => {
                      control.change(control.value ? '' : option.value)
                    }}
                    value={option.value}
                  />
                </div>
              )}
            />
          ))}
        </div>
        {fields.contactInquiryType.errors && (
          <InputError data-testid="contact-types-error">
            {fields.contactInquiryType.errors}
          </InputError>
        )}
      </FieldWrapper>

      <div className="flex flex-col gap-y-6">
        <FieldWrapper>
          <FieldLabel htmlFor={fields.name.id} label="お名前" required />
          <TextInput
            errorMessages={fields.name.errors}
            id={fields.name.id}
            name={fields.name.name}
            placeholder="例：山田太郎"
          />
        </FieldWrapper>

        <FieldWrapper>
          <FieldLabel
            htmlFor={fields.organization.id}
            label="貴社名・団体名"
            required
            subText="個人の方は「個人」と記入してください"
          />
          <TextInput
            errorMessages={fields.organization.errors}
            id={fields.organization.id}
            name={fields.organization.name}
            placeholder="例：株式会社TranceTECH Capital"
          />
        </FieldWrapper>

        <FieldWrapper>
          <FieldLabel
            htmlFor={fields.email.id}
            label="メールアドレス"
            required
          />
          <TextInput
            errorMessages={fields.email.errors}
            id={fields.email.id}
            name={fields.email.name}
            placeholder="例：sample@trancetechcapital.co.jp"
          />
        </FieldWrapper>

        <FieldWrapper>
          <FieldLabel
            htmlFor={fields.phone.id}
            label="電話番号（ハイフンなし）"
            required
          />
          <TextInput
            errorMessages={fields.phone.errors}
            id={fields.phone.id}
            name={fields.phone.name}
            placeholder="例：08012345678"
          />
        </FieldWrapper>

        <FieldWrapper>
          <FieldLabel htmlFor={fields.content.id} label="お問い合わせ内容" />
          <Textarea
            errorMessages={fields.content.errors}
            id={fields.content.id}
            name={fields.content.name}
            placeholder="例：オフィスの広さや設備について教えてください"
            rows={8}
          />
        </FieldWrapper>

        <div className="flex flex-col gap-y-1">
          <div className="inline-flex items-center justify-center gap-x-2">
            <Checkbox
              checked={checkboxControl.value === 'on'}
              className="h-5 w-5 align-middle"
              data-testid="agree-checkbox"
              label=""
              name={fields.agree.name}
              onChange={() =>
                checkboxControl.change(
                  checkboxControl.value === 'on' ? 'off' : 'on',
                )
              }
              value={checkboxControl.value}
            />
            <p>
              <Link href="/terms" rel="noopener noreferrer" target="_blank">
                利用規約
              </Link>
              <span>・</span>
              <Link
                href="/privacy-policy"
                rel="noopener noreferrer"
                target="_blank"
              >
                プライバシーポリシー
              </Link>
              <span>に同意</span>
            </p>
          </div>
          {fields.agree.errors && (
            <InputError data-testid="agree-error">
              {fields.agree.errors}
            </InputError>
          )}
        </div>

        <button
          className="h-[60px] w-full rounded bg-cta text-body-bold text-white"
          type="submit"
        >
          送信する
        </button>
      </div>
    </Form>
  )
}
